import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import { useAuth } from '../../contexts/AuthContext';
import confetti from 'canvas-confetti';
import { EmailService } from '../../services/emailService';

const AuthModal = ({ isOpen, onClose, mode, onModeChange }) => {
  const [formData, setFormData] = useState({ email: '', password: '', passwordConfirm: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);

  // Effect for success confetti
  useEffect(() => {
    if (showSuccess) {
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 3000 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) return clearInterval(interval);

        const particleCount = 50 * (timeLeft / duration);
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
      }, 250);

      return () => clearInterval(interval);
    }
  }, [showSuccess]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
    setResetMessage('');
  };

  const handleClose = () => {
    setFormData({ email: '', password: '', passwordConfirm: '', name: '' });
    setError('');
    setSuccess('');
    setResetMessage('');
    setShowSuccess(false);
    onClose();
  };

  const handleSuccessRedirect = () => {
    handleClose();
    navigate('/mon-compte');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (mode === 'login') {
        const result = await login(formData.email.trim(), formData.password);
        if (result.success) {
          handleClose();
          navigate('/mon-compte', { state: { celebration: 'login' }, replace: true });
        } else {
          const raw = result?.error || '';
          const msg = (typeof raw === 'string' && raw.trim() === 'Failed to authenticate.')
            ? 'Email ou mot de passe incorrect.'
            : raw;
          setError(msg || 'Email ou mot de passe incorrect.');
        }
      } else if (mode === 'reset') {
        if (!formData.email) { setError('Veuillez saisir votre email.'); setLoading(false); return; }
        try {
          setResetLoading(true);
          await pb.collection('users').requestPasswordReset(formData.email.trim());
          setError('');
          setSuccess("Un email de réinitialisation vous a été envoyé si cette adresse existe.");
        } catch (e) {
          setError("Impossible d'envoyer l'email de réinitialisation. Réessayez plus tard.");
        } finally {
          setResetLoading(false);
        }
      } else {
        if (formData.password !== formData.passwordConfirm) {
          setError('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }
        const result = await register(formData.email.trim(), formData.password, formData.passwordConfirm, formData.name);
        if (result.success) {
          // Auto-login after registration
          await login(formData.email.trim(), formData.password);

          setSuccess(result.message);
          // TRIGGER WELCOME EMAIL SIMULATION
          EmailService.sendWelcome({ email: formData.email });

          setFormData({ email: '', password: '', passwordConfirm: '', name: '' });

          // Trigger confetti immediately
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });

          setShowSuccess(true);
        } else {
          setError(result.error);
        }
      }
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    }
    setLoading(false);
  };

  if (showSuccess) {
    return (
      <div className="auth-modal-overlay" onClick={handleSuccessRedirect}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', padding: '50px 30px' }}>
          <div style={{ marginBottom: 25 }}>
            <div style={{
              width: 90, height: 90, background: '#dcfce7', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto',
              boxShadow: '0 10px 25px -5px rgba(22, 163, 74, 0.4)'
            }}>
              <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, margin: '0 0 12px', color: 'var(--text-color)' }}>Félicitations !</h2>
          <p style={{ margin: '0 0 35px', color: 'var(--text-color)', opacity: 0.8, fontSize: '1.15rem', lineHeight: '1.5' }}>
            Votre compte a été créé avec succès.<br />Bienvenue chez InfoColab.
          </p>
          <button
            onClick={handleSuccessRedirect}
            className="submit-btn"
            style={{ width: 'auto', padding: '14px 36px', fontSize: '1.05rem' }}
          >
            Accéder à mon compte
          </button>

          <button
            className="close-btn"
            onClick={handleSuccessRedirect}
            aria-label="Fermer"
            style={{ position: 'absolute', top: 20, right: 20, padding: 8 }}
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2>{mode === 'login' ? 'Connexion' : mode === 'register' ? 'Inscription' : 'Réinitialiser le mot de passe'}</h2>
          <button className="close-btn" onClick={handleClose} aria-label="Fermer">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'register' && (
            <div className="form-group">
              <label htmlFor="name">Nom complet</label>
              <input id="name" name="name" value={formData.name} onChange={handleChange} required />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="ex: nom@domaine.com"
              autoComplete="email"
              inputMode="email"
            />
          </div>
          {mode !== 'reset' && (
            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              <div className="password-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Votre mot de passe"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  onClick={() => setShowPassword(p => !p)}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7c-3.26 0-6.02-1.86-7.73-3.73" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9.9 9.9A3 3 0 0012 15a3 3 0 002.1-.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                  )}
                </button>
              </div>
              <div className={`password-hint${formData.password && formData.password.length < 6 ? ' error' : ''}`}>
                Minimum 6 caractères
              </div>
            </div>
          )}
          {mode === 'register' && (
            <div className="form-group">
              <label htmlFor="passwordConfirm">Confirmer le mot de passe</label>
              <input id="passwordConfirm" name="passwordConfirm" type="password" value={formData.passwordConfirm} onChange={handleChange} required minLength={6} />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          {resetMessage && !error && !success && (<div className="info-message">{resetMessage}</div>)}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Chargement…' : mode === 'login' ? 'Se connecter' : mode === 'register' ? "S'inscrire" : 'Envoyer le lien de réinitialisation'}
          </button>
        </form>

        <div className="auth-switch">
          {mode === 'login' ? (
            <>
              <p>
                Pas encore de compte ?{' '}
                <NavLink to="/inscription" className="switch-btn">S'inscrire</NavLink>
              </p>
              <p style={{ marginTop: 8 }}>
                <button type="button" className="switch-btn" onClick={() => onModeChange('reset')}>Mot de passe oublié ?</button>
              </p>
            </>
          ) : mode === 'register' ? (
            <p>Déjà un compte ? <button type="button" className="switch-btn" onClick={() => onModeChange('login')}>Se connecter</button></p>
          ) : (
            <p>Vous vous souvenez de votre mot de passe ? <button type="button" className="switch-btn" onClick={() => onModeChange('login')}>Revenir à la connexion</button></p>
          )}
        </div>

        <style>{`
          .auth-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:2000;padding:20px}
          .auth-modal{background:var(--light-color,#fff);color:var(--text-color,#111);border-radius:14px;width:100%;max-width:420px;max-height:90vh;overflow:auto;box-shadow:0 18px 60px rgba(0,0,0,.22);border:1px solid var(--border-color-extra-light,#e5e7eb);opacity:1;transform:translateY(0);transition:opacity .2s ease, transform .2s ease}
          .auth-modal-header{display:flex;justify-content:space-between;align-items:center;padding:22px 22px 0;border-bottom:1px solid var(--border-color-extra-light,#e5e7eb);margin-bottom:20px}
          .auth-modal-header h2{margin:0;font-size:1.5rem;font-weight:700}
          .close-btn{background:none;border:none;cursor:pointer;color:var(--text-color);padding:6px;border-radius:8px;transition:background-color .15s ease, transform .08s ease}
          .close-btn:hover{background:rgba(0,0,0,.07)}
          .close-btn:active{transform:scale(.98)}
          .auth-form{padding:0 22px 22px}
          .form-group{margin-bottom:14px}
          .form-group label{display:block;margin-bottom:6px;font-weight:600}
          .form-group input{width:100%;box-sizing:border-box;padding:12px 14px;border:2px solid var(--border-color-extra-light,#e5e7eb);border-radius:10px;font-size:1rem;font-family:inherit;line-height:1.5;background:var(--light-color,#fff);color:var(--text-color);transition:border-color .15s ease, box-shadow .15s ease}
          .form-group input:focus{outline:none;border-color:var(--primary-color,#007bff);box-shadow:0 0 0 4px rgba(0,123,255,.12)}
          .password-wrapper{position:relative}
          .password-wrapper input{padding-right:40px}
          .password-toggle{position:absolute;top:50%;right:10px;transform:translateY(-50%);background:none;border:none;padding:4px;border-radius:6px;color:var(--text-color);opacity:.7;cursor:pointer}
          .password-toggle:hover{opacity:1;background:rgba(0,0,0,.06)}
          .password-hint{font-size:.78rem;opacity:.75;margin-top:6px}
          .password-hint.error{opacity:1;color:#dc2626}
          .error-message{background:#fef2f2;color:#dc2626;padding:12px 16px;border-radius:8px;margin-bottom:12px;border-left:4px solid #dc2626}
          .success-message{background:#f0fdf4;color:#16a34a;padding:12px 16px;border-radius:8px;margin-bottom:12px;border-left:4px solid #16a34a}
          .info-message{background:#eff6ff;color:#1d4ed8;padding:10px 14px;border-radius:8px;margin-bottom:12px;border-left:4px solid #93c5fd}
          .submit-btn{width:100%;background:linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%);color:#fff;border:none;padding:14px 22px;border-radius:10px;font-size:1rem;font-weight:700;cursor:pointer;margin-bottom:16px;box-shadow:0 8px 16px rgba(29,78,216,.2);transition:filter .15s ease, transform .08s ease}
          .submit-btn:hover{filter:brightness(.97)}
          .submit-btn:active{transform:translateY(1px)}
          .auth-switch{text-align:center;padding:18px 22px 22px;border-top:1px solid var(--border-color-extra-light,#e5e7eb);margin-top:10px}
          .switch-btn{background:none;border:none;color:var(--primary-color,#007bff);font-weight:700;cursor:pointer;text-decoration:underline}
          body.dark .auth-modal{background:var(--light-color,#222);color:var(--text-color,#eaeaea);box-shadow:0 18px 60px rgba(0,0,0,.5)}
        `}</style>
      </div>
    </div>
  );
};

export default AuthModal;
